import { inject, Injectable, signal } from '@angular/core';

import { catchError, EMPTY, filter, last, map, Observable, switchMap, tap, throwError } from 'rxjs';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpRequest,
} from '@angular/common/http';
import { Video } from '@shared/models/video/video.model';
import { MibUploadFile } from '@medinbox/angular';
import { S3MultipartUploadService } from './s3-multipart-upload.service';
import { formatUploadError } from '@shared/utils/upload-error.util';
import { MultipartUpload, PartUpload } from '@shared/models/upload-part';
import { AnalyticsService, VideoApiService } from '@core/services';
import { AnalyticsEvents } from '@shared/models';

/**
 * Orchestrates the case library upload workflow.
 * Coordinates between file upload UI, S3 upload service, and video API.
 * Follows Single Responsibility Principle - focuses on orchestration, not implementation details.
 */
@Injectable({
  providedIn: 'root',
})
export class CaseLibraryService {
  private readonly httpClient = inject(HttpClient);
  private readonly s3MultipartUploadService = inject(S3MultipartUploadService);
  private readonly videoApiService = inject(VideoApiService);
  private readonly analytics = inject(AnalyticsService);

  private readonly uploadInProgress = signal<{ file: File; caseId: string }[]>([]);

  /*
   Held here and not in the dialog, which an import outlives. Keyed on the `File` itself and never
   on its name: two recordings from two folders can carry the same one.
  */
  private readonly uploads = signal<readonly MibUploadFile[]>([]);

  private readonly canceledCaseIds = new Set<string>();

  /** The imports in flight, for whoever draws them. */
  readonly files = this.uploads.asReadonly();

  /** Takes what the reader chose and starts an import for each, reporting as they go. */
  startUpload(files: readonly File[]): void {
    if (files.length === 0) {
      return;
    }

    this.uploads.update((list) => [
      ...list,
      ...files.map((file) => ({
        name: file.name,
        size: file.size,
        state: 'uploading' as const,
        file,
      })),
    ]);

    /* One subscription each: a `forkJoin` would hold the slowest one over every other row. */
    files.forEach((file) =>
      this.createCaseRecord(file)
        .pipe(
          tap((video) =>
            this.uploadInProgress.update((list) => [...list, { file, caseId: video.id }]),
          ),
          switchMap((video) => this.uploadFile(file, video)),
        )
        .subscribe(),
    );
  }

  /** Shows what the component turned away, so a refusal is read rather than guessed. */
  showRejected(rejected: readonly MibUploadFile[]): void {
    if (rejected.length > 0) {
      this.uploads.update((list) => [...list, ...rejected]);
    }
  }

  /** The row goes, and the case with it while still uploading. Bytes in flight are not recalled. */
  cancelUpload(file: File): void {
    const started = this.uploadInProgress().find((item) => item.file === file);
    const uploading = this.uploads().some(
      (item) => item.file === file && item.state === 'uploading',
    );

    this.forget(file);

    if (started && uploading) {
      this.analytics.trackEvent(AnalyticsEvents.CaseUploadCanceled);
      this.canceledCaseIds.add(started.caseId);
      this.delete(started.caseId).subscribe();
    }
  }

  /** Whether this tab canceled the upload of that case; answers once, then forgets it. */
  wasCanceledHere(caseId: string): boolean {
    return this.canceledCaseIds.delete(caseId);
  }

  /** Drops a row once it has nothing left to say. */
  forget(file: File): void {
    this.uploads.update((list) => list.filter((item) => item.file !== file));
    this.uploadInProgress.update((list) => list.filter((item) => item.file !== file));
  }

  /** Drops every row that has finished, done or failed, so the next opening starts clean. */
  clearFinished(): void {
    this.uploads.update((list) => list.filter((item) => item.state === 'uploading'));
    this.uploadInProgress.update((list) =>
      list.filter(({ file }) => this.uploads().some((item) => item.file === file)),
    );
  }

  /** What became of one file, written where the list can be read from anywhere. */
  private report(file: File, changes: Partial<MibUploadFile>): void {
    this.uploads.update((list) =>
      list.map((item) => (item.file === file ? { ...item, ...changes } : item)),
    );
  }

  /**
   * Deletes a video record from the server and track deletion event.
   *
   * @param videoId - The ID of the video to delete
   * @returns Observable that completes when the video is deleted
   */
  delete(videoId: string): Observable<void> {
    return this.videoApiService.delete(videoId).pipe(
      tap(() => {
        this.analytics.trackEvent(AnalyticsEvents.CaseDeleted, { case_id: videoId });
        this.uploadInProgress.update((list) => list.filter((item) => item.caseId !== videoId));
      }),
    );
  }

  /**
   * Edit a video record and track edition event
   * @param videoId - The ID of the video to delete
   * @param video - Partial video fields to update
   * @returns Observable emitting the updated video record
   */
  edit(videoId: string, video: Partial<Video>): Observable<Video> {
    return this.videoApiService
      .update(videoId, video)
      .pipe(tap(() => this.analytics.trackEvent(AnalyticsEvents.CaseEdited, { case_id: videoId })));
  }

  /**
   * Creates a new video record.
   *
   * @param file - The video file to upload
   * @returns Observable that emits the created video record containing the presigned url list required for the upload process
   */
  private createCaseRecord(file: File): Observable<Video> {
    return this.videoApiService.create(file.name, file.size).pipe(
      catchError((error: HttpErrorResponse) => {
        const message = `Error while creating the video record: ${formatUploadError(error)}`;
        this.report(file, {
          state: 'error',
          error: 'An error occurred while preparing the upload',
        });
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * Uploads a file to a presigned S3 URL with progress tracking.
   * Supports both simple uploads (≤100MB) and multipart uploads (>100MB).
   *
   * @param mibFile - The file to upload
   * @param videoCase - The video case record containing the presigned S3 URL(s) to upload to
   * @returns Observable that completes when upload is finished
   * @throws Error if the upload fails or is cancelled
   */
  private uploadFile(file: File, videoCase: Video): Observable<void> {
    // Determine upload strategy based on backend response
    if (videoCase.presignedUrl) {
      return this.uploadSimple(file, videoCase);
    } else if (videoCase.presignedMultiPartsUrl && videoCase.uploadId) {
      const multipartUpload: MultipartUpload = {
        id: videoCase.id,
        presignedMultiPartsUrl: videoCase.presignedMultiPartsUrl,
        uploadId: videoCase.uploadId,
      };
      return this.uploadMultipart(file, multipartUpload);
    }

    // No upload URLs provided
    return EMPTY;
  }

  /**
   * Uploads a file using simple single-part upload (for files ≤100MB).
   *
   * @param mibFile - The file to upload
   * @param videoCase - The video case record containing the presigned S3 URL
   * @returns Observable that completes when upload is finished
   */
  private uploadSimple(file: File, videoCase: Video): Observable<void> {
    const contentType = file.type || 'application/octet-stream';

    const headers = new HttpHeaders({
      'Content-Type': contentType,
    });

    const req = new HttpRequest('PUT', videoCase.presignedUrl!, file, {
      headers,
      reportProgress: true,
      responseType: 'text',
    });

    return this.httpClient.request(req).pipe(
      tap((event: HttpEvent<unknown>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress: {
            const total = event.total ?? file.size ?? 1;
            const progress = Math.round((100 * (event.loaded ?? 0)) / total);
            this.report(file, { progress });
            break;
          }
          case HttpEventType.Response:
            this.report(file, { progress: 100, state: 'done' });
            break;
        }
      }),
      filter((event): event is HttpEvent<unknown> => event.type === HttpEventType.Response),
      last(),
      switchMap(() => this.completeSimpleUpload(videoCase)),
      catchError((error: HttpErrorResponse) => {
        this.trackUploadError(file);
        const message = `Error while uploading the case: ${formatUploadError(error)}`;
        console.error(message);
        return this.delete(videoCase.id);
      }),
    );
  }

  /**
   * Uploads a file using multipart upload (for files >100MB).
   * Delegates to S3MultipartUploadService for the actual upload logic.
   *
   * @param mibFile - The file to upload
   * @param videoCase - The video case record containing the presigned multipart URLs
   * @returns Observable that completes when all parts are uploaded and merged
   */
  private uploadMultipart(file: File, multiPartUpload: MultipartUpload): Observable<void> {
    return this.s3MultipartUploadService
      .uploadMultipart(file, multiPartUpload.presignedMultiPartsUrl, (progress) =>
        this.report(file, { progress }),
      )
      .pipe(
        tap(() => this.report(file, { progress: 100, state: 'done' })),
        switchMap((parts) => this.completeMultipartUpload(multiPartUpload, parts)),
        catchError((error) => {
          this.trackUploadError(file);
          console.error(`Error while uploading multipart case: ${formatUploadError(error)}`);
          return this.delete(multiPartUpload.id);
        }),
      );
  }

  /**
   * Notifies the backend that a simple upload is complete.
   *
   * @param videoCase - The video case record
   * @returns Observable that completes when the backend is notified
   */
  private completeSimpleUpload(videoCase: Video): Observable<void> {
    return this.videoApiService.update(videoCase.id, { uploaded: true }).pipe(
      tap(() => this.trackUploadCompletion()),
      map(() => void 0),
    );
  }

  /**
   * Notifies the backend that a multipart upload is complete and provides part ETags.
   *
   * @param multipartUpload - The multipart upload record containing the upload ID and presigned URLs
   * @param parts - Array of part numbers and their corresponding ETags
   * @returns Observable that completes when the backend merges all parts
   */
  private completeMultipartUpload(
    multipartUpload: MultipartUpload,
    parts: PartUpload[],
  ): Observable<void> {
    return this.videoApiService
      .completeMultipartUpload(multipartUpload.id, multipartUpload.uploadId, parts)
      .pipe(tap(() => this.trackUploadCompletion()));
  }

  private trackUploadCompletion(): void {
    this.analytics.trackEvent(AnalyticsEvents.CaseUploaded);
  }

  /** Marks the row as failed and tracks the event. */
  private trackUploadError(file: File): void {
    this.report(file, {
      state: 'error',
      error: 'An error occurred while uploading the case',
    });
    this.analytics.trackEvent(AnalyticsEvents.CaseUpdloadError);
  }
}
